import { useCallback, useEffect, useRef, useState } from 'react';
import { trpClient } from '../client/trpClient';
import { useAuth } from '../contexts/AuthContext';
import { useRFQ } from '../contexts/RFQContext';
import type { RFQ } from '../types/rfq.types';
import { displayRfqId, sameRfqId } from '../utils/rfqIdentity';

/**
 * App-wide handoff notifications: a toast when the technical steps of an RFQ
 * are shared WITH the signed-in user, and a toast back to the sharer/creator
 * when the end user signs off.
 *
 * Fed by one feed: a poll of the RFQ list while the tab is visible, which
 * derives both events from the durable handoff state on each RFQ. Polling
 * stops while the tab is hidden and runs immediately when it comes back, so a
 * backgrounded tab costs nothing. Every event has a stable key derived from
 * the handoff timestamps, and shown keys persist in localStorage per account —
 * so each event toasts once per browser.
 */

export interface HandoffNotice {
  key: string;
  rfqId: string;
  kind: 'fill' | 'signed-off';
  title: string;
  message: string;
}

const POLL_MS = 30_000;
const AUTO_DISMISS_MS = 15_000;
const SEEN_CAP = 200;

const seenStorageKey = (email: string) => `handoffNoticesSeen:${email}`;

const loadSeen = (email: string): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(seenStorageKey(email)) || '[]'));
  } catch {
    return new Set();
  }
};

const saveSeen = (email: string, seen: Set<string>) => {
  try {
    localStorage.setItem(seenStorageKey(email), JSON.stringify(Array.from(seen).slice(-SEEN_CAP)));
  } catch {
    // Storage full/blocked — worst case the same toast shows again next session.
  }
};

const fillKey = (rfqId: string, sharedAt?: string) =>
  `fill:${rfqId}:${sharedAt ? new Date(sharedAt).getTime() : 0}`;
const signoffKey = (rfqId: string, signedOffAt?: string) =>
  `signoff:${rfqId}:${signedOffAt ? new Date(signedOffAt).getTime() : 0}`;

/** Derives a notice from the durable handoff state carried by an RFQ. */
const noticeFromRfq = (rfq: RFQ, myEmail: string, myUserId?: string): HandoffNotice | null => {
  const status = rfq.handoff?.status;
  const endUserEmail = rfq.endUser?.email?.toLowerCase();
  const isMine = Boolean(myUserId && rfq.createdBy === myUserId);
  // Falls back to the id for an unnamed RFQ, and this lands in a message a
  // person reads, so it has to be the display form.
  const projectName = rfq.name || displayRfqId(rfq.rfqId);

  if (status === 'PENDING' && endUserEmail === myEmail && !isMine) {
    return {
      key: fillKey(rfq.rfqId, rfq.handoff?.sharedAt),
      rfqId: rfq.rfqId,
      kind: 'fill',
      title: 'Technical details needed',
      message: `${rfq.createdByName || 'A teammate'} shared "${projectName}" with you — the technical questions are yours to fill.`,
    };
  }
  const sharedByMe = Boolean(myUserId && (rfq.handoff?.sharedBy === myUserId || isMine));
  if (status === 'SIGNED_OFF' && sharedByMe && endUserEmail !== myEmail) {
    const who = rfq.endUser?.name || rfq.endUser?.email || 'The end user';
    return {
      key: signoffKey(rfq.rfqId, rfq.handoff?.signedOffAt),
      rfqId: rfq.rfqId,
      kind: 'signed-off',
      title: 'Signed off',
      message: `${who} signed off "${projectName}" — the technical details are ready.`,
    };
  }
  return null;
};

export const useHandoffNotifications = () => {
  const { backendUser } = useAuth();
  const myEmail = backendUser?.email?.toLowerCase();
  const myUserId = backendUser?.userId;

  const [notices, setNotices] = useState<HandoffNotice[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  // A handoff event about the RFQ currently on screen refreshes it in place,
  // so banners flip (pending → signed off, view-only → over to you) without
  // the viewer navigating. Held in a ref: the poll effect must not restart
  // every time the open RFQ changes.
  const { currentRFQ, fetchRFQ } = useRFQ();
  const openRfqRef = useRef<{ id?: string; refresh: typeof fetchRFQ }>({ refresh: fetchRFQ });
  useEffect(() => {
    openRfqRef.current = { id: currentRFQ?.rfqId, refresh: fetchRFQ };
  }, [currentRFQ?.rfqId, fetchRFQ]);

  const dismiss = useCallback((key: string) => {
    setNotices((prev) => prev.filter((n) => n.key !== key));
    const timer = timersRef.current.get(key);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(key);
    }
  }, []);

  useEffect(() => {
    if (!myEmail) return undefined;
    const email = myEmail;
    let cancelled = false;

    const show = (incoming: (HandoffNotice | null)[]) => {
      const seen = loadSeen(email);
      const fresh = incoming.filter((n): n is HandoffNotice => Boolean(n && !seen.has(n.key)));
      if (fresh.length === 0) return;
      fresh.forEach((n) => seen.add(n.key));
      saveSeen(email, seen);
      setNotices((prev) => [...prev, ...fresh.filter((f) => !prev.some((p) => p.key === f.key))]);
      fresh.forEach((n) => {
        timersRef.current.set(n.key, window.setTimeout(() => dismiss(n.key), AUTO_DISMISS_MS));
      });

      const open = openRfqRef.current;
      if (open.id && fresh.some((n) => sameRfqId(n.rfqId, open.id))) {
        // Silent + preserveStep: refresh the fact, never move the viewer.
        open.refresh(open.id, true, true);
      }
    };

    const poll = async () => {
      try {
        const response = await trpClient.getRFQs(1, 100);
        if (cancelled || !response.success) return;
        show(response.data.items.map((rfq) => noticeFromRfq(rfq, email, myUserId)));
      } catch {
        // Best-effort — the next tick covers it.
      }
    };

    // A hidden tab holds no timer and issues no request; coming back polls at
    // once so the viewer never waits a tick for a toast they already earned.
    let interval: number | undefined;
    const stopPolling = () => {
      if (interval === undefined) return;
      window.clearInterval(interval);
      interval = undefined;
    };
    const startPolling = () => {
      poll();
      if (interval === undefined) interval = window.setInterval(poll, POLL_MS);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') startPolling();
      else stopPolling();
    };

    onVisibilityChange();
    document.addEventListener('visibilitychange', onVisibilityChange);

    const timers = timersRef.current;
    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [myEmail, myUserId, dismiss]);

  return { notices, dismiss };
};

export default useHandoffNotifications;
